import { defineToolHandler } from "./handler";
import { PhysicsDiceID, tools } from "../../defs";
import { h, useRef, useState } from "@lukekaalim/act";
import { html } from "@lukekaalim/act-web";
import { assetsURLs } from "../../assets";
import { a3 } from "@lukekaalim/act-three";
import { ObjectClone, renderBeat, Snapshot, useDisposable } from "@kosmod/graphics-3d";

import * as three from 'three';
import { CursorSurfacePreview } from "./Preview";
import { TheatreState } from "../../hooks";
import { SlotStateIcon } from "./ToolSlotRenderer";

defineToolHandler(tools.dice.roll, {
  def: tools.dice.roll,

  onInput(event, state, theatre, logger, eventNative) {
    if (event.type !== 'mouse' || event.button !== 'primary')
      return;

    const position = theatre.cursorPosition.get();
    
    if (position) {
      logger.log('Adding Dice')
      theatre.controller.addPhysicsDie(state.diceTemplateId, {
        position: { x: position.vector.x, y: 1, z: position.vector.y },
        rotation: { x: 0, y: 0, z: 0, w: 1 },
      })
    }
  },

  ToolbeltComponent({ toolState, theatre }) {
    const diceTemplate = theatre.data.diceTemplates.get(toolState.diceTemplateId);

    if (!diceTemplate)
      return 'd?';

    const model = theatre.sceneRes.dieMeshes.get(toolState.diceTemplateId);

    if (!model)
      return null;


    return [
      h(SlotStateIcon, {}, diceTemplate.name),
      h(Snapshot, { object: model, graphics: theatre.graphics })
    ];
  },

  PreviewComponent({ theatre, toolState }) {
    const model = theatre.sceneRes.dieMeshes.get(toolState.diceTemplateId);

    if (!model)
      return null;

    return h(CursorSurfacePreview, { state: theatre, offset: { x: 0, y: 1, z: 0 } },
      h(ObjectClone, { target: model, scale: { x: .2, y: .2, z: .2 }, position: new three.Vector3() })
    )
  }
})

const eqSet = <T>(xs: Set<T>, ys: Set<T>) =>
  xs.size === ys.size &&
  [...xs].every((x) => ys.has(x));

defineToolHandler(tools.dice.cleanup, {
  def: tools.dice.cleanup,

  onInput(event, state, theatre, logger, eventNative) {
    if (event.type === 'mouse' && event.button === 'primary') {
      const cursor = theatre.cursorPosition.get();
      if (!cursor)
        return;

      const allDice = [...theatre.data.ownedPhysicsDice, ...theatre.data.remotePhysicsDice];

      for (const die of allDice) {
        let transform = theatre.data.remotePhysicsDieTransforms.get(die.id);
        if (!transform)
          transform = theatre.localDiceTransforms.get(die.id)!;

        const position = new three.Vector2(transform.position.x, transform.position.z);
        const cursorPosition = new three.Vector2(cursor.vector.x, cursor.vector.y);

        if (cursorPosition.distanceTo(position) < state.radius) {
          theatre.controller.removePhysicsDie(die.id);
        }
      }
    }
  },


  ToolbeltComponent({ toolState, theatre }) {
    return h(html.img, { src: assetsURLs.icons.cleanup })
  },
  PreviewComponent({ toolState, theatre, material, cursorPosition }) {

    const [targetDie, setTargetDie] = useState<Set<PhysicsDiceID>>(new Set());
  
    const allDice = [...theatre.data.ownedPhysicsDice, ...theatre.data.remotePhysicsDice];

    renderBeat.useBeat(() => {
      const cursorTheatrePosition = cursorPosition.get();
      if (!cursorTheatrePosition)
        return;

      const collidingDie = new Set<PhysicsDiceID>();

      for (const die of allDice) {
        let transform = theatre.data.remotePhysicsDieTransforms.get(die.id);
        if (!transform)
          transform = theatre.localDiceTransforms.get(die.id)!;

        const position = new three.Vector2(transform.position.x, transform.position.z);
        const cursorVector = new three.Vector2(cursorTheatrePosition.vector.x, cursorTheatrePosition.vector.y);

        if (cursorVector.distanceTo(position) < toolState.radius) {
          collidingDie.add(die.id)
        }

        if (!eqSet(targetDie, collidingDie))
          setTargetDie(collidingDie);
      }
    })

    const { wires: geometry } = useDisposable(() => {
      const cylinder = new three.CylinderGeometry(toolState.radius, toolState.radius, .5, 24, 1, true);
      const wires = new three.WireframeGeometry(cylinder);

      return {
        wires,
        dispose() {
          cylinder.dispose();
          wires.dispose()
        },
      }
    }, [])

    return [
      h(CursorSurfacePreview, { state: theatre, offset: { x: 0, y: .25, z: 0} },
        h(a3.lineSegments, { geometry, material: new three.LineBasicMaterial({
          color: material instanceof three.MeshStandardMaterial ? material.color : 'white'
        }) })),
      [...targetDie].map(physicsDieId => {
        return h(PhysicsDieCage, { die: physicsDieId, state: theatre, key: physicsDieId });
      })
    ]
  }
})

const PhysicsDieCage = ({ die, state }: { die: PhysicsDiceID, state: TheatreState }) => {
  const physicsDie = state.data.ownedPhysicsDice.find(d => d.id === die) || state.data.remotePhysicsDice.find(d => d.id === die);

  const diceTemplate = physicsDie && state.data.diceTemplates.get(physicsDie.dieTemplate);

  if (!diceTemplate)
    return null;

  const mesh = state.sceneRes.dieMeshes.get(diceTemplate.id);
  if (!mesh)
    return null;
  
  const clone = mesh.clone();
  clone.scale.set(.2, .2, .2);

  const box = new three.Box3().setFromObject(clone);

  const ref = useRef<three.Box3Helper | null>(null);

  renderBeat.useBeat(() => {
    const box3Helper = ref.get();
    if (!box3Helper)
      return;

    const transform = state.localDiceTransforms.get(physicsDie.id) || state.data.remotePhysicsDieTransforms.get(physicsDie.id);
    
    if (!transform)
      return;


    clone.position.copy(transform.position);
    clone.quaternion.copy(transform.rotation);
    box.setFromObject(clone);
    // box3Helper.quaternion.copy(transform.rotation)

    // box3Helper.updateMatrix();
  })

  return h(a3.Box3Helper, { box, ref })
}